import { useEffect, useMemo, useState } from 'react';

import { TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';

import type { PolicyAlertWithIdSchema } from '@/lib/heyapi/types.gen';

interface AlertBannerProps {
  alert: PolicyAlertWithIdSchema;
  setShowAcknowledgeDialog: (show: boolean) => void;
  setSelectedAlertId: (alertId: number) => void;
  isPending: boolean;
  selectedAlertId: number | null;
  policyId: string;
}

export const AlertBanner = ({
  alert,
  isPending,
  selectedAlertId,
  setShowAcknowledgeDialog,
  setSelectedAlertId,
  policyId,
}: AlertBannerProps) => {
  const [isSnoozed, setIsSnoozed] = useState(false);

  const handleAcknowledgeClick = () => {
    setShowAcknowledgeDialog(true);
    setSelectedAlertId(alert.id);
  };

  useEffect(() => {
    const snoozed = sessionStorage.getItem(
      `banner-snoozed-${alert.id}-${policyId}`
    );
    setIsSnoozed(snoozed === 'true');
  }, [alert.id, policyId]);

  const handleSnooze = () => {
    sessionStorage.setItem(`banner-snoozed-${alert.id}-${policyId}`, 'true');
    setIsSnoozed(true);
  };

  const shouldShowAlertBanner = useMemo(() => {
    const isAcknowledged = alert.acknowledged;

    if (isAcknowledged) return false;

    // check if the banner is snoozed
    return !isSnoozed;
  }, [alert.acknowledged, isSnoozed]);

  const isLoading = selectedAlertId === alert.id && isPending;

  if (!shouldShowAlertBanner) return null;

  return (
    <div
      key={alert.id}
      className="mt-[1px] w-full border-b border-yellow-400 bg-yellow-400 px-4 py-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <TriangleAlert className="h-5 w-5 flex-shrink-0 text-yellow-900" />
          <p className="text-sm font-medium text-yellow-900">{alert.message}</p>
        </div>
        {alert.type !== 'parent_cancelled' ? (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleSnooze}
              variant="outline"
              disabled={isLoading}
            >
              Snooze
            </Button>
            <Button
              type="button"
              onClick={handleAcknowledgeClick}
              variant="outline"
              disabled={isLoading}
            >
              {isLoading ? 'Acknowledging...' : 'Acknowledge'}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
